import React, { FC, ReactNode } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { useAppState } from 'src/shared/appState';
import { backofficeUrl, getPathName, T } from 'src/shared/helpers';
import ChevronRightIcon from 'src/shared/material_icons/navigation/chevron_right.svg';
import { styled } from 'styled-components';
import { Icon } from '../icon';
import type { ItemMenu } from '../leftAppSidebar/Menu';
import type { breadcumbTreeMenuQuery } from './__generated__/breadcumbTreeMenuQuery.graphql';

const BreadcrumbNav = styled.nav`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    color: #78716c;
    font-family: ${(props) => props.theme.app.font};
`;

const BreadcrumbLink = styled.button`
    color: #78716c;
    text-decoration: none;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    appearance: none;

    &:hover {
        color: #263238;
        text-decoration: underline;
    }
`;

const BreadcrumbSeparator = styled(Icon)`
    color: #78716c;
    width: 16px;
    height: 16px;
    min-width: 16px;
`;

const BreadcrumbCurrent = styled.span`
    color: #78716c;
    font-weight: 600;
`;

export const BreadcumbContainer = BreadcrumbNav;

const breadcumbTreeMenuQuery = graphql`
    query breadcumbTreeMenuQuery($lastUsedTool: Int!) {
        treeMenu(lastUsedTool: $lastUsedTool)
    }
`;

const SKIPPED_MENU_KEYS = new Set(['quick-access']);
const HOME_BREADCUMB_KEY = 'home';

interface BreadcumbTrailItem {
    key: string;
    label: ReactNode;
    urlPage?: string;
    isExternalUrl?: boolean;
}

export interface BreadcumbExtraItem {
    key?: string;
    label: ReactNode;
    urlPage?: string;
    isExternalUrl?: boolean;
}

const hasNavigationUrl = (item: Pick<BreadcumbTrailItem, 'urlPage'>) => {
    const url = item.urlPage?.trim();
    return Boolean(url && !/^\d+$/.test(url));
};

const findMenuPathByKey = (rootMenu: ItemMenu, pageKey: string): ItemMenu[] => {
    let bestPath: ItemMenu[] = [];

    const walk = (item: ItemMenu, path: ItemMenu[]) => {
        if (SKIPPED_MENU_KEYS.has(item.key)) return;

        const nextPath = [...path, item];
        if (item.key === pageKey && nextPath.length > bestPath.length) {
            bestPath = nextPath;
        }

        for (const child of item.menu || []) {
            walk(child, nextPath);
        }
    };

    for (const item of rootMenu.menu || []) {
        walk(item, []);
    }

    return bestPath;
};

const getFallbackHomeItem = (): BreadcumbTrailItem => ({
    key: HOME_BREADCUMB_KEY,
    label: T._('Home', 'Admin'),
    urlPage: backofficeUrl('/')
});

const menuPathToTrailItems = (path: ItemMenu[]): BreadcumbTrailItem[] => {
    if (path.length === 0) return [getFallbackHomeItem()];

    const trailItems = path.map((item) => ({
        key: item.key,
        label: item.name,
        urlPage: item.key === HOME_BREADCUMB_KEY ? backofficeUrl('/') : item.menu?.length > 0 ? backofficeUrl(`/${item.key}`) : item.urlPage,
        isExternalUrl: item.key === HOME_BREADCUMB_KEY ? false : item.isExternalUrl
    }));

    if (trailItems[0]?.key !== HOME_BREADCUMB_KEY) {
        return [getFallbackHomeItem(), ...trailItems];
    }

    return trailItems;
};

const buildTrailItems = (rootMenu: ItemMenu, pageKey: string, extraItems: BreadcumbExtraItem[] = [], currentLabel?: ReactNode): BreadcumbTrailItem[] => {
    const path = findMenuPathByKey(rootMenu, pageKey);

    const trailItems: BreadcumbTrailItem[] = [
        ...menuPathToTrailItems(path),
        ...extraItems.map((item, index) => ({
            key: item.key ?? `${pageKey}-extra-${index}`,
            label: item.label,
            urlPage: item.urlPage,
            isExternalUrl: item.isExternalUrl
        }))
    ];

    if (currentLabel) {
        trailItems.push({
            key: `${pageKey}-current`,
            label: currentLabel
        });
    }

    return trailItems;
};

const requestNavigation = (detail: Record<string, unknown>) => {
    const navigationEvent = new CustomEvent('app-navigate-request', {
        detail,
        cancelable: true
    });
    window.dispatchEvent(navigationEvent);
    return !navigationEvent.defaultPrevented;
};

const getNavigationDetail = (urlPage: string) => ({
    path: getPathName(urlPage),
    urlPage,
    isDam: window.location.href.toLowerCase().includes('dam')
});

interface BreadcumbContentProps {
    className?: string;
    items: BreadcumbTrailItem[];
    onClick?: () => void;
}

const BreadcumbContent: FC<BreadcumbContentProps> = ({ className, items, onClick }) => {
    const navigate = useNavigate();

    const handleItemClick = (item: BreadcumbTrailItem) => (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        if (onClick && item.key === items[0]?.key && item.key !== HOME_BREADCUMB_KEY) {
            if (!requestNavigation({ action: onClick })) return;
            onClick();
            return;
        }

        if (!item.urlPage) return;
        if (!requestNavigation(getNavigationDetail(item.urlPage))) return;

        if (item.isExternalUrl) {
            window.open(item.urlPage, '_blank');
            return;
        }

        navigate(getPathName(item.urlPage));
    };

    return (
        <BreadcrumbNav className={className} aria-label='Breadcrumb'>
            {items.map((item, index) => {
                const isCurrent = index === items.length - 1;
                const canNavigate = !isCurrent && (hasNavigationUrl(item) || (onClick && index === 0));

                return (
                    <React.Fragment key={item.key || index}>
                        {index > 0 && (
                            <BreadcrumbSeparator aria-hidden='true'>
                                <ChevronRightIcon />
                            </BreadcrumbSeparator>
                        )}
                        {canNavigate ? (
                            <BreadcrumbLink type='button' onClick={handleItemClick(item)}>
                                {item.label}
                            </BreadcrumbLink>
                        ) : (
                            <BreadcrumbCurrent>{item.label}</BreadcrumbCurrent>
                        )}
                    </React.Fragment>
                );
            })}
        </BreadcrumbNav>
    );
};

interface BreadcumbProps {
    pageKey?: string;
    currentLabel?: ReactNode;
    extraItems?: BreadcumbExtraItem[];
    onClick?: () => void;
    className?: string;
    children?: React.ReactNode;
}

interface BreadcumbFromMenuProps extends BreadcumbProps {
    pageKey: string;
}

const BreadcumbFromMenu: FC<BreadcumbFromMenuProps> = ({ pageKey, currentLabel, extraItems, className, onClick }) => {
    const { state } = useAppState();
    const data = useLazyLoadQuery<breadcumbTreeMenuQuery>(breadcumbTreeMenuQuery, { lastUsedTool: state.lastUsedTool }, { fetchPolicy: 'store-and-network' });
    const rootMenu = JSON.parse(data.treeMenu) as ItemMenu;

    return <BreadcumbContent className={className} items={buildTrailItems(rootMenu, pageKey, extraItems, currentLabel)} onClick={onClick} />;
};

export const Breadcumb: FC<BreadcumbProps> = (props) => {
    if (props.pageKey) {
        return <BreadcumbFromMenu {...props} pageKey={props.pageKey} />;
    }

    const extraItems = (props.extraItems ?? []).map((item, index) => ({
        key: item.key ?? `extra-${index}`,
        label: item.label,
        urlPage: item.urlPage,
        isExternalUrl: item.isExternalUrl
    }));
    const items =
        props.currentLabel || props.children
            ? [...[getFallbackHomeItem(), ...extraItems], { key: 'current', label: props.currentLabel ?? props.children }]
            : [getFallbackHomeItem(), ...extraItems];

    return <BreadcumbContent className={props.className} items={items} onClick={props.onClick} />;
};
